// Hours & Overtime policy client — the per-legal-entity, config-driven rule that
// turns worked HOURS (overtime / public-holiday / night) into pay. Plus the
// worked-hours import (file → staged, priced at run time). Talks via the gateway.
import { gatewayApi } from '@/lib/hrApi';

const BASE = '/api/v1';

export type BasisMode = 'divisor' | 'explicit';
export type HourMode = 'factor' | 'flat_per_hour';

export interface HourType {
  code: string;
  label: string;
  mode: HourMode;
  factor?: number;
  rate?: number;
  taxable: boolean;
}

export type HoursSource = '' | 'file' | 'attendance' | 'assignment' | 'work';

export interface HoursPolicy {
  version: number;
  hourly_basis: { mode: BasisMode; divisor?: number; rate?: number };
  hours_source?: HoursSource; // legacy single value (kept for old saved policies)
  // Set form: which sources this entity prices ({file, attendance, assignment}).
  // Empty/absent = auto (price whatever is staged). Wins over hours_source.
  hours_sources?: Exclude<HoursSource, ''>[];
  hour_types: HourType[];
}

/** The effective accepted-source set (set form, else legacy single, else []=auto). */
export const effectiveHoursSources = (p: HoursPolicy): Exclude<HoursSource, ''>[] => {
  if (p.hours_sources && p.hours_sources.length) return p.hours_sources;
  if (p.hours_source) return [p.hours_source];
  return [];
};

export const getHoursPolicy = (legalEntityId: string) =>
  gatewayApi.get<HoursPolicy>(`${BASE}/legal-entities/${legalEntityId}/hours-policy`).then((r) => r.data);

export const setHoursPolicy = (legalEntityId: string, policy: HoursPolicy) =>
  gatewayApi.put<HoursPolicy>(`${BASE}/legal-entities/${legalEntityId}/hours-policy`, policy).then((r) => r.data);

// --- worked-hours import ---
export interface ImportHoursRow {
  employee_id?: string;
  email?: string;
  name?: string;
  hours: Record<string, string>; // hour-type code → hours
}
export interface ImportHoursResult {
  dry_run: boolean;
  period: string;
  summary: { total: number; staged: number; skipped: number };
  results: { row: number; email?: string; name?: string; action: string; errors: string[]; warnings: string[] }[];
}
export const importHours = (body: { dry_run: boolean; period: string; rows: ImportHoursRow[] }) =>
  gatewayApi.post<ImportHoursResult>(`${BASE}/payroll/import-hours`, body).then((r) => r.data);

// Pull an hours sheet from a connected source (Google Sheet / hosted CSV URL) into
// the SAME {headers, rows} shape a file upload produces — the "system integration"
// path, so a time-clock/team sheet needs no manual download.
export const fetchHoursSource = (body: { connector: string; url?: string; sheet_id?: string; gid?: string }) =>
  gatewayApi
    .post<{ headers: string[]; rows: string[][] }>(`${BASE}/payroll/hours/fetch`, body)
    .then((r) => r.data);

// Price one bucket client-side for the live preview (mirrors the Go engine).
export function previewAmount(policy: HoursPolicy, code: string, hours: number, basic: number): number | null {
  const ht = policy.hour_types.find((h) => h.code.toUpperCase() === code.toUpperCase());
  if (!ht) return null;
  if (ht.mode === 'flat_per_hour') return hours * (Number(ht.rate) || 0);
  const basis = policy.hourly_basis;
  const hourly = basis.mode === 'explicit' ? Number(basis.rate) || 0 : basic / (Number(basis.divisor) || 1);
  return hours * hourly * (Number(ht.factor) || 0);
}

// ---- Work hours-source review (no-silent-pay) --------------------------------
// Completed work items stage hours as PROPOSED — invisible to the calc engine
// until approved here (then priced only if the entity opted into the source).
export interface ProposedHour {
  id: string; employeeId: string; hourType?: string; hours?: string;
  source?: string; periodMonth: string; sourceEvent: string; createdAt: string;
}
export const listProposedHours = async (): Promise<ProposedHour[]> =>
  (await gatewayApi.get<{ proposed: ProposedHour[] }>('/api/v1/payroll/hours/proposed')).data.proposed || [];
export const decideProposedHours = async (ids: string[], approve: boolean): Promise<number> =>
  (await gatewayApi.post<{ updated: number }>('/api/v1/payroll/hours/proposed/decide', { ids, approve })).data.updated;
